import type { ReactNode } from 'react';
import './cabecalho-tela.css';

export type VarianteDoCabecalho = 'padrao' | 'identidade';

export type CabecalhoDeTelaProps = {
  /** O único obrigatório. Vira o `<h1>` da tela. */
  titulo: string;
  /**
   * A contagem do ESCOPO, ao lado do título — "38 de 1.284".
   *
   * `descricaoDaContagem` vira o `title`: um número grande ao lado de uma
   * lista curta faz a pessoa procurar o que falta, e a frase responde antes de
   * a dúvida existir.
   */
  contagem?: ReactNode;
  descricaoDaContagem?: string;
  /** Um controle pequeno colado ao título: o ícone de ligar para a pessoa. */
  aoLadoDoTitulo?: ReactNode;
  /** A frase que diz o que a tela FAZ. Some no telefone. */
  apoio?: ReactNode;
  /** A linha de FATOS: de onde veio, desde quando, de quem é. Fica no telefone. */
  meta?: ReactNode;
  /** As ações da TELA. Nunca as de um item dela. */
  acoes?: ReactNode;
  /** Fileira própria, em largura cheia: contatos, filtros de período. */
  extra?: ReactNode;
  /** A seta de voltar, quando a tela foi aberta de dentro de outra. */
  voltar?: ReactNode;
  /** O avatar, quando o assunto da tela é uma pessoa. */
  retrato?: ReactNode;
  variante?: VarianteDoCabecalho;
};

/**
 * O cabeçalho de tela.
 *
 * Eram OITO definições no produto para o mesmo papel — ver o cabeçalho da
 * folha. Aqui elas viram uma.
 *
 * ── Tudo opcional, menos o título ────────────────────────────────────────
 *
 * É o que faz o mesmo componente servir a "Painel" e ao nome de um cliente com
 * avatar, etiquetas e dois contatos. Uma variante por tela levaria de volta às
 * oito.
 *
 * ── O `<h1>` mora sempre dentro da linha do título ───────────────────────
 *
 * Mesmo sem contagem. É a linha que dá a sapata comum ao título e ao número:
 * sem ela, acrescentar a contagem depois mudaria o alinhamento do título que
 * já estava certo.
 *
 * ── Ele NÃO rola ─────────────────────────────────────────────────────────
 *
 * `flex: none` — quem rola é o irmão de baixo. A tela que o usa precisa ser um
 * palco `flex-direction: column` com um filho `overflow-y: auto`; do contrário
 * o cabeçalho sobe junto com a lista e leva as ações da tela com ele.
 */
export function CabecalhoDeTela({
  titulo,
  contagem,
  descricaoDaContagem,
  aoLadoDoTitulo,
  apoio,
  meta,
  acoes,
  extra,
  voltar,
  retrato,
  variante = 'padrao',
}: CabecalhoDeTelaProps) {
  return (
    <header className={variante === 'padrao' ? 'cab' : `cab cab--${variante}`}>
      {voltar}
      {retrato}

      <div className="cab__texto">
        <div className="cab__titulo-linha">
          <h1 className="cab__titulo">{titulo}</h1>
          {aoLadoDoTitulo !== undefined && <span className="cab__ao-lado">{aoLadoDoTitulo}</span>}
          {contagem !== undefined && (
            <span className="cab__contagem" title={descricaoDaContagem}>
              {contagem}
            </span>
          )}
        </div>
        {apoio !== undefined && <p className="cab__apoio">{apoio}</p>}
        {meta !== undefined && <div className="cab__meta">{meta}</div>}
      </div>

      {acoes !== undefined && <div className="cab__acoes">{acoes}</div>}
      {extra !== undefined && <div className="cab__extra">{extra}</div>}
    </header>
  );
}
